import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useRef } from 'react';
import type { CuratedHeroStory, HeroTheme } from '../../data/curatedHeroStories';
import { useMotionPreferences } from '../../motion/preferences';

gsap.registerPlugin(useGSAP);

export interface EpisodeBrandLens {
  id: string;
  title: string;
  value: string;
  connection: string;
}

export const FEATURED_EPISODE_LENSES: readonly EpisodeBrandLens[] = [
  {
    id: '2391',
    title: '旅の出発点が、家族で戻る場所になる',
    value: '驚きに、出会う場所',
    connection: '海・移動・時間の継続',
  },
  {
    id: '1575',
    title: '一人の記憶が、三世代の時間へ続く',
    value: '余白 — Yohaku',
    connection: '家族・学び・世代の継承',
  },
  {
    id: '1810',
    title: '働く日の途中に、海辺で呼吸できる',
    value: 'ビジネスリゾート',
    connection: '仕事・食事・海の景色',
  },
  {
    id: '2357',
    title: 'イベントの記憶が、次の世代を連れてくる',
    value: '驚きに、出会う場所',
    connection: 'イベント・交流・次の来館',
  },
] as const;

const THEME_LABEL: Record<HeroTheme, string> = {
  memory: '記憶',
  family: '家族',
  sea: '海',
  events: 'イベント',
  daily: '日常',
  request: '要望',
  critical: '課題',
};

const FALLBACK_LENS: Record<HeroTheme, Omit<EpisodeBrandLens, 'id'>> = {
  memory: { title: '時間を越えて残る場所の記憶', value: '余白 — Yohaku', connection: '記憶・時間' },
  family: { title: '人と世代の時間が重なる場所', value: '共創と挑戦で未来を紡ぐ', connection: '家族・つながり' },
  sea: { title: '都市の仕事と海辺の時間が隣り合う', value: 'ビジネスリゾート', connection: '海・余白' },
  events: { title: '一日の体験が、次の来館理由になる', value: '驚きに、出会う場所', connection: 'イベント・交流' },
  daily: { title: '特別な場所が、日常の選択肢になる', value: 'ビジネスリゾート', connection: '日常・滞在' },
  request: { title: '期待の言葉が、次の改善を照らす', value: '向上と挑戦', connection: '要望・改善' },
  critical: { title: '厳しい声も、信頼をつくる起点になる', value: '信頼関係の醸成', connection: '課題・対話' },
};

function brandLensFor(hero: CuratedHeroStory): EpisodeBrandLens {
  return FEATURED_EPISODE_LENSES.find((item) => item.id === hero.id)
    ?? { id: hero.id, ...FALLBACK_LENS[hero.theme] };
}

export function EpisodeSpotlight({
  heroes,
  heroIndex,
  onHeroIndexChange,
  onOpenCollection,
}: {
  heroes: CuratedHeroStory[];
  heroIndex: number;
  onHeroIndexChange(index: number): void;
  onOpenCollection(): void;
}) {
  const root = useRef<HTMLElement>(null);
  const { reducedMotion } = useMotionPreferences();
  const hero = heroes[heroIndex]!;
  const lens = brandLensFor(hero);
  const featured = FEATURED_EPISODE_LENSES.some((item) => item.id === hero.id);

  useGSAP(() => {
    const element = root.current;
    if (!element) return;
    const targets = gsap.utils.selector(element)('[data-episode-reveal]');
    const horizon = gsap.utils.selector(element)('[data-episode-horizon]');
    if (reducedMotion) {
      gsap.set([...targets, ...horizon], { autoAlpha: 1, clearProps: 'all' });
      return;
    }
    const timeline = gsap.timeline({ defaults: { ease: 'power3.out', overwrite: 'auto' } });
    timeline
      .fromTo(horizon, { scaleX: 0, transformOrigin: 'left center' }, { scaleX: 1, duration: 0.55 }, 0)
      .fromTo(targets, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 0.42, stagger: 0.045 }, 0.08);
    return () => timeline.kill();
  }, { scope: root, dependencies: [hero.id, reducedMotion], revertOnUpdate: true });

  return <article
    ref={root}
    className="relative mt-10 grid min-h-[88svh] content-center overflow-hidden rounded-[2.25rem] bg-atc-navy px-6 py-12 text-white shadow-atc-card md:px-10 lg:px-14"
    aria-labelledby="episode-spotlight-heading"
  >
    <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-60" viewBox="0 0 1200 720" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="episode-sea" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1a60a2" stopOpacity=".18" />
          <stop offset="1" stopColor="#ffffff" stopOpacity=".03" />
        </linearGradient>
      </defs>
      <circle cx="1015" cy="170" r="88" fill="none" stroke="white" strokeOpacity=".13" />
      <circle cx="1015" cy="170" r="132" fill="none" stroke="white" strokeOpacity=".07" />
      <path d="M0 455 C235 405 380 510 602 458 C828 405 979 494 1200 432 L1200 720 L0 720Z" fill="url(#episode-sea)" />
      <path data-episode-horizon d="M0 455 C235 405 380 510 602 458 C828 405 979 494 1200 432" fill="none" stroke="white" strokeOpacity=".22" strokeWidth="1.5" />
      <path data-episode-horizon d="M0 500 C250 450 395 550 625 502 C830 459 1010 535 1200 478" fill="none" stroke="white" strokeOpacity=".09" />
    </svg>

    <div className="relative">
      <header className="flex flex-wrap items-start justify-between gap-5">
        <div data-episode-reveal>
          <p className="text-xs font-bold tracking-[.2em] text-white/55">ATC EPISODE — EMOTIONAL CORE</p>
          <h3 id="episode-spotlight-heading" className="mt-3 text-2xl font-bold tracking-[-.03em] md:text-4xl">数字の外に残った、ATC。</h3>
        </div>
        <div data-episode-reveal className="text-right">
          <p className="text-xs font-bold text-white/55">{featured ? 'ATCらしさを映す4つの記憶' : '監査済みヒーローセレクション'}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{String(heroIndex + 1).padStart(2, '0')} <span className="text-sm text-white/45">/ {heroes.length}</span></p>
        </div>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,.65fr)] lg:items-end">
        <div>
          <div data-episode-reveal className="flex flex-wrap gap-2">
            <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-bold">{THEME_LABEL[hero.theme]}</span>
            <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-bold">原文のまま</span>
          </div>
          <blockquote
            data-episode-reveal
            aria-live="polite"
            className="mt-7 max-w-5xl whitespace-pre-wrap text-xl font-medium leading-[2] tracking-[.01em] md:text-[1.7rem]"
          >
            {hero.text}
          </blockquote>
          <p data-episode-reveal className="mt-6 text-sm font-bold text-white/65">{hero.gender} · {hero.age} · {hero.frequency}</p>
        </div>

        <aside data-episode-reveal className="rounded-3xl border border-white/15 bg-white/8 p-5 backdrop-blur-sm" aria-label="ブランドとの接点">
          <p className="text-[11px] font-bold tracking-[.16em] text-white/55">ブランドとの接点</p>
          <p className="mt-3 text-xl font-bold leading-relaxed">{lens.title}</p>
          <div className="mt-5 border-t border-white/15 pt-4">
            <p className="text-xs text-white/55">ATCのブランド概念</p>
            <p className="mt-1 font-bold">{lens.value}</p>
            <p className="mt-3 text-xs text-white/55">声に現れた要素</p>
            <p className="mt-1 text-sm font-bold">{lens.connection}</p>
          </div>
          <p className="mt-5 text-[11px] leading-relaxed text-white/45">編集部による統合。エピソード本文そのものをブランド評価へ置き換えるものではありません。</p>
        </aside>
      </div>

      <footer className="mt-10 border-t border-white/15 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex flex-wrap gap-2" aria-label="最もATCらしい4つの記憶を選ぶ">
            {FEATURED_EPISODE_LENSES.map((item, index) => <button
              type="button"
              key={item.id}
              aria-pressed={hero.id === item.id}
              onClick={() => onHeroIndexChange(index)}
              className={`grid h-11 min-w-11 place-items-center rounded-full border px-3 text-sm font-bold transition ${hero.id === item.id ? 'border-white bg-white text-atc-navy' : 'border-white/25 text-white hover:bg-white/10'}`}
            >
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <span className="sr-only">注目エピソード {index + 1}</span>
            </button>)}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={heroIndex === 0} onClick={() => onHeroIndexChange(heroIndex - 1)} className="rounded-full border border-white/25 px-4 py-2 text-sm font-bold disabled:opacity-30">前の記憶</button>
            <button type="button" disabled={heroIndex === heroes.length - 1} onClick={() => onHeroIndexChange(heroIndex + 1)} className="rounded-full bg-white px-4 py-2 text-sm font-bold text-atc-navy disabled:opacity-30">次の記憶</button>
            <button type="button" onClick={onOpenCollection} className="rounded-full border border-white/25 px-4 py-2 text-sm font-bold hover:bg-white/10">全1,633件へ</button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-[repeat(18,minmax(0,1fr))] gap-1" aria-hidden="true">
          {heroes.map((item, index) => <span key={`${item.id}:${item.source}`} className={`h-1 rounded-full ${index <= heroIndex ? 'bg-white' : 'bg-white/15'}`} />)}
        </div>
        <p className="mt-4 text-xs text-white/50">自動再生はしません。読み終えるまで、本文は変わりません。</p>
      </footer>
    </div>
  </article>;
}
